import { useCallback, useContext } from "react";
import {
    updateBillExtractionDetails,
    updateInvoiceExtractionDetails,
} from "@api/financialDocument.api";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    accrualDocumentBeingProcessed,
    getAllAccrualLineItemExtractions,
} from "./accrualImporter.helpers";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { AccrualImporterDocumentRow } from "./accrualImporter.types";
import { isSameCounterpartyName } from "./draftDetails/matchExtractedCounterparty";

interface MatchingDraft {
    financialDocumentId: number;
    extractionId: string;
}

export function findUnassignedDraftsByCounterpartyName({
    documentRows,
    mode,
    names,
    excludeExtractionId,
}: {
    documentRows: AccrualImporterDocumentRow[];
    mode: AccrualDocumentImporterMode;
    names: string[];
    excludeExtractionId?: string;
}): MatchingDraft[] {
    return documentRows.flatMap(({ financialDocument }) => {
        if (
            !financialDocument ||
            accrualDocumentBeingProcessed(financialDocument)
        ) {
            return [];
        }
        return getAllAccrualLineItemExtractions(financialDocument, mode)
            .filter(
                (extraction) =>
                    extraction.id !== excludeExtractionId &&
                    extraction.counterpartyId == null &&
                    names.some((name) =>
                        isSameCounterpartyName(
                            extraction.counterpartyName ?? "",
                            name,
                        ),
                    ),
            )
            .map((extraction) => ({
                financialDocumentId: financialDocument.id,
                extractionId: extraction.id,
            }));
    });
}

export function useAssignCounterpartyToMatchingDrafts() {
    const { mode, documentRows, revalidateDocument } = useContext(
        AccrualImporterContext,
    );

    return useCallback(
        async ({
            counterparty,
            extractedName,
            excludeExtractionId,
        }: {
            counterparty: { id: string; name: string };
            extractedName?: string | null;
            excludeExtractionId?: string;
        }) => {
            const drafts = findUnassignedDraftsByCounterpartyName({
                documentRows,
                mode,
                names: [counterparty.name, extractedName ?? ""],
                excludeExtractionId,
            });
            await Promise.allSettled(
                drafts.map(({ financialDocumentId, extractionId }) => {
                    const body = {
                        financialDocumentId,
                        counterpartyId: counterparty.id,
                        counterpartyName: counterparty.name,
                    };
                    return mode === "bills"
                        ? updateBillExtractionDetails({
                              ...body,
                              billExtractionId: extractionId,
                          })
                        : updateInvoiceExtractionDetails({
                              ...body,
                              invoiceExtractionId: extractionId,
                          });
                }),
            );
            new Set(
                drafts.map(({ financialDocumentId }) => financialDocumentId),
            ).forEach((financialDocumentId) =>
                revalidateDocument({ financialDocumentId }),
            );
        },
        [documentRows, mode, revalidateDocument],
    );
}
